import { FC, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { Cancel, CheckCircle } from "@mui/icons-material";
import { TComplianceTemplate } from "../../../apis/compliance/compliance.interfaces";
import { useApplyTemplateHook } from "../../../hooks/useComplianceHook";
import { logger } from "../../../utils/common";
import { useInstituteContext } from "../../../context/institute.context";

interface TemplateSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  templates: TComplianceTemplate[];
  isLoading?: boolean;
}

const TemplateSelectionDialog: FC<TemplateSelectionDialogProps> = ({
  open,
  onClose,
  templates,
  isLoading = false,
}) => {
  const { selectedInstitute } = useInstituteContext();
  const applyTemplateMutation = useApplyTemplateHook();
  const [selectedTemplate, setSelectedTemplate] =
    useState<TComplianceTemplate | null>(null);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "regulatory":
        return "error";
      case "institutional":
        return "primary";
      case "accreditation":
        return "success";
      default:
        return "default";
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplate || !selectedInstitute) {
      logger.show_error("Please select a template and an institute");
      return;
    }

    try {
      await applyTemplateMutation.mutateAsync({
        templateId: selectedTemplate.id,
        instituteId: selectedInstitute.id,
      });
      logger.show_success(
        `Template "${selectedTemplate.name}" applied successfully!`,
      );
      onClose();
      setSelectedTemplate(null);
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to apply template. Please try again.",
      );
    }
  };

  const isApplying = applyTemplateMutation.isPending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="template-selection-dialog"
    >
      <DialogTitle data-testid="template-selection-dialog-title">
        Select Compliance Template
      </DialogTitle>
      <DialogContent data-testid="template-selection-content">
        {applyTemplateMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {applyTemplateMutation.error?.message ||
              "Failed to apply template. Please try again."}
          </Alert>
        )}

        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
            data-testid="template-loading-container"
          >
            <CircularProgress />
          </Box>
        ) : templates.length === 0 ? (
          <Alert severity="info" data-testid="template-empty-state">
            No templates available.
          </Alert>
        ) : (
          <Grid container spacing={2} data-testid="template-list-container">
            {templates.map((template) => (
              <Grid item xs={12} key={template.id}>
                <Card
                  sx={{
                    cursor: "pointer",
                    border: selectedTemplate?.id === template.id ? 2 : 1,
                    borderColor:
                      selectedTemplate?.id === template.id
                        ? "primary.main"
                        : "divider",
                    "&:hover": {
                      borderColor: "primary.main",
                      boxShadow: 2,
                    },
                  }}
                  onClick={() => setSelectedTemplate(template)}
                  data-testid={`template-card-${template.id}`}
                >
                  <CardContent>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mb={1}
                    >
                      <Typography variant="h6" component="div">
                        {template.name}
                      </Typography>
                      {selectedTemplate?.id === template.id && (
                        <CheckCircle color="primary" />
                      )}
                    </Box>

                    {template.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {template.description}
                      </Typography>
                    )}

                    <Box display="flex" gap={1} mb={2}>
                      <Chip
                        label={
                          template.category.charAt(0).toUpperCase() +
                          template.category.slice(1)
                        }
                        color={getCategoryColor(template.category) as any}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={`${template.compliances.length} compliance${template.compliances.length !== 1 ? "s" : ""}`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>

                    <Typography variant="subtitle2" gutterBottom>
                      Includes:
                    </Typography>
                    <List dense sx={{ py: 0 }}>
                      {template.compliances
                        .slice(0, 3)
                        .map((compliance, index) => (
                          <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                            <ListItemText
                              primary={compliance.name}
                              secondary={compliance.description}
                              primaryTypographyProps={{ variant: "body2" }}
                              secondaryTypographyProps={{ variant: "caption" }}
                            />
                          </ListItem>
                        ))}
                      {template.compliances.length > 3 && (
                        <ListItem sx={{ py: 0.5, px: 0 }}>
                          <ListItemText
                            primary={`...and ${template.compliances.length - 3} more`}
                            primaryTypographyProps={{
                              variant: "caption",
                              color: "text.secondary",
                            }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <DialogActions data-testid="template-dialog-actions">
        <Button
          onClick={onClose}
          disabled={isApplying}
          startIcon={<Cancel />}
          data-testid="cancel-template-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleApplyTemplate}
          variant="contained"
          disabled={!selectedTemplate || isApplying}
          startIcon={
            isApplying ? <CircularProgress size={16} /> : <CheckCircle />
          }
          data-testid="apply-template-button"
        >
          {isApplying ? "Applying..." : "Apply Template"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateSelectionDialog;
